/**
 * @description Global Settings page - main container for merchant settings
 *
 * Key features:
 * - Category-based navigation (Time Clock, Invoicing, etc.)
 * - Dynamic panel rendering based on available settings
 * - Permission-gated access (settings.view required)
 * - Multi-company support via CompanyContext
 *
 * ENTERPRISE ARCHITECTURE NOTES:
 * ==============================
 * 1. MULTI-COMPANY MERCHANTS:
 *    - Settings scoped to current company from CompanyContext
 *    - Company switcher in header allows toggling
 *    - Each company has isolated settings
 *
 * 2. DYNAMIC VISIBILITY:
 *    - Only shows categories with available settings
 *    - Settings filtered by feature flags (is_default_enabled)
 *    - Time Clock settings are DEFAULT ENABLED for all merchants
 *
 * 3. CATEGORY PANELS:
 *    - Each category has a dedicated panel component
 *    - Panels render settings with appropriate UI controls
 *    - Schema-based rendering for flexibility
 *
 * @dependencies
 * - settingsService: API calls
 * - PermissionGate: RBAC UI gating
 * - useCompany: Current company context
 *
 * @see .claude/tasks/Global_Settings_Architecture.md
 */

import React, { useState, useEffect, useCallback, useRef, Fragment } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Settings, Clock, FileText, Bell, AlertCircle, Check, ChevronRight, X, Calendar, CalendarDays, MapPin, Info, ChevronDown, Building2, Sun, Moon, Mail } from 'lucide-react';
import { Tab, Listbox, Transition } from '@headlessui/react';
import { useCompany } from '../../contexts/CompanyContext';
import { useBranding } from '../../contexts/BrandingContext';
import { resolveComposeFromEmail } from '../../utils/composeFromEmail';
import PermissionGate from '../shared/PermissionGate';
import settingsService from '../../services/api/settingsService';
import locationService from '../../services/api/locationService';
import TimeClockSettingsPanel from './TimeClockSettingsPanel';
import PTOSettingsPanel from './PTOSettingsPanel';
import HolidaysSettingsPanel from './HolidaysSettingsPanel';
import { getComplianceMessage, hasComplianceRequirements, COMPLIANCE_STATES } from '../../constants/complianceStates';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Category metadata for display
const CATEGORY_CONFIG = {
  time_clock: {
    label: 'Time Clock',
    icon: Clock,
    description: 'Configure break allowances, clock-in rules, and overtime settings'
  },
  pto: {
    label: 'Paid Time Off',
    icon: Calendar,
    description: 'Configure default PTO allocations and approval policies'
  },
  holidays: {
    label: 'Holidays',
    icon: CalendarDays,
    description: 'Enable US federal observances, set holiday pay defaults, and preview the company calendar'
  },
  invoicing: {
    label: 'Invoicing',
    icon: FileText,
    description: 'Set default due dates, reminder schedules, and invoice preferences'
  },
  notifications: {
    label: 'Notifications',
    icon: Bell,
    description: 'Manage email and notification preferences'
  },
  email_sender: {
    label: 'Email Sender',
    icon: Mail,
    description: 'Configure the display name, reply-to address, and default options for outgoing emails'
  }
};

/**
 * Insert Holidays after PTO (or Time Clock) so it appears as a sibling nav item
 * even though holidays are API resources, not settings-schema keys.
 * @param {string[]} categories
 * @returns {string[]}
 */
function withHolidaysCategory(categories) {
  if (categories.includes('holidays')) return categories;
  const next = [...categories];
  const ptoIdx = next.indexOf('pto');
  const timeIdx = next.indexOf('time_clock');
  const insertAt =
    ptoIdx >= 0 ? ptoIdx + 1 : timeIdx >= 0 ? timeIdx + 1 : 0;
  next.splice(insertAt, 0, 'holidays');
  return next;
}

const GlobalSettingsPage = () => {
  const { company } = useCompany();
  const { darkMode, toggleDarkMode, occupancy } = useBranding();
  const composeFromEmail = resolveComposeFromEmail({ occupancy, company });
  const [searchParams, setSearchParams] = useSearchParams();
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Location state - for multi-location companies
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [loadingLocations, setLoadingLocations] = useState(true);

  // Compliance notice state (triggered from location management)
  const [complianceNotice, setComplianceNotice] = useState(null);

  // Draft state for ALL settings across ALL categories
  const [draftValues, setDraftValues] = useState({});
  const [saving, setSaving] = useState(false);

  // Holidays are API resources (company_holidays), not settings draft keys.
  // Panel registers save/discard; dirty drives the shared sticky bar.
  const [holidaysDirty, setHolidaysDirty] = useState(false);
  const holidaysActionsRef = useRef({
    save: async () => {},
    discard: () => {},
  });
  const handleHolidaysDirtyChange = useCallback((dirty) => {
    setHolidaysDirty(Boolean(dirty));
  }, []);
  const handleRegisterHolidaysActions = useCallback((actions) => {
    holidaysActionsRef.current = {
      save: actions?.save || (async () => {}),
      discard: actions?.discard || (() => {}),
    };
  }, []);

  // Track sidebar state and window width for sticky bar positioning
  // Note: MerchantLayout uses 'merchantSidebarOpen' as the storage key
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    const stored = localStorage.getItem('merchantSidebarOpen');
    return stored !== null ? JSON.parse(stored) : true;
  });
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Listen for sidebar changes and window resize
  useEffect(() => {
    const handleStorageChange = () => {
      const stored = localStorage.getItem('merchantSidebarOpen');
      setSidebarOpen(stored !== null ? JSON.parse(stored) : true);
    };
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    // Poll at reasonable interval for sidebar state (200ms is sufficient for smooth UX)
    const interval = setInterval(handleStorageChange, 200);
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('resize', handleResize);
    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Load locations for the company
  const loadLocations = useCallback(async () => {
    if (!company?.id) return;

    setLoadingLocations(true);
    try {
      const response = await locationService.getLocations();
      const locs = response.locations || [];
      setLocations(locs);

      // Check if a specific location was passed in URL
      // URL param takes priority - always use it if present (for "Configure" link)
      const locationIdFromUrl = searchParams.get('locationId');
      const locationFromUrl = locationIdFromUrl
        ? locs.find(l => l.id === locationIdFromUrl)
        : null;

      // Set initial selection: URL param ALWAYS takes priority > current selection > primary > first
      if (locationFromUrl) {
        setSelectedLocation(locationFromUrl);
      } else if (!selectedLocation) {
        // Only set default if nothing is selected yet
        const primary = locs.find(l => l.isPrimary);
        setSelectedLocation(primary || locs[0] || null);
      }
    } catch (err) {
      console.error('Error loading locations:', err);
    } finally {
      setLoadingLocations(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.id, searchParams]);

  useEffect(() => {
    loadLocations();
  }, [loadLocations]);

  // Check for compliance notice from URL params (triggered from location management)
  useEffect(() => {
    const complianceState = searchParams.get('compliance');
    const locationName = searchParams.get('location');
    const locationId = searchParams.get('locationId');

    if (complianceState && hasComplianceRequirements(complianceState)) {
      const stateInfo = COMPLIANCE_STATES[complianceState];
      setComplianceNotice({
        state: complianceState,
        stateName: stateInfo?.name || complianceState,
        locationName: locationName || 'your location',
        message: getComplianceMessage(complianceState)
      });
    }

    // Clear the URL params after reading them (but keep locationId for selection)
    if (complianceState || locationName) {
      searchParams.delete('compliance');
      searchParams.delete('location');
      // Keep locationId to select the right location, but clear it after a moment
      if (locationId) {
        setTimeout(() => {
          searchParams.delete('locationId');
          setSearchParams(searchParams, { replace: true });
        }, 100);
      } else {
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [searchParams, setSearchParams]);

  // Load all settings - includes location context for multi-location companies
  const loadSettings = useCallback(async () => {
    if (!company?.id) return;

    setLoading(true);
    setError(null);
    try {
      // Pass locationId for location-scoped settings resolution
      // When a location is selected, settings with allow_location_override=true
      // will show location-specific values instead of company-level values
      const locationId = locations.length > 1 ? selectedLocation?.id : null;
      const data = await settingsService.getAllSettings(locationId);
      setSettings(data);
    } catch (err) {
      console.error('Error loading settings:', err);
      setError('Failed to load settings. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [company?.id, selectedLocation?.id, locations.length]);

  // Load settings on mount and when location changes
  useEffect(() => {
    // Only load after locations have been fetched
    if (!loadingLocations) {
      loadSettings();
    }
  }, [loadSettings, loadingLocations]);

  // Clear draft values when location changes (prevent saving wrong location's settings)
  useEffect(() => {
    setDraftValues({});
  }, [selectedLocation?.id]);

  // Get visible categories (those with at least one setting) + Holidays (API-backed)
  const visibleCategories = withHolidaysCategory(
    Object.keys(settings).filter((cat) => settings[cat]?.length > 0)
  );

  // Current active category based on selected tab index
  const activeCategory = visibleCategories[selectedIndex] || visibleCategories[0];

  // Flatten all settings for easy lookup
  const allSettings = Object.values(settings).flat();

  // Check if current category has ANY settings that support location override
  // Used to determine whether to show location dropdown or "All Locations"
  const currentCategorySettings = settings[activeCategory] || [];
  const categorySupportsLocationOverride = currentCategorySettings.some(
    s => s.allowLocationOverride === true
  );

  // Get server value for a setting
  const getServerValue = useCallback((key) => {
    const setting = allSettings.find(s => s.key === key);
    return setting?.currentValue;
  }, [allSettings]);

  // Get current value (draft if exists, otherwise server)
  const getValue = useCallback((key) => {
    if (key in draftValues) {
      return draftValues[key];
    }
    return getServerValue(key);
  }, [draftValues, getServerValue]);

  // Update draft value (doesn't save to server)
  const updateDraft = useCallback((key, value) => {
    setDraftValues(prev => ({ ...prev, [key]: value }));
  }, []);

  // Check if there are unsaved changes (settings drafts and/or holidays panel)
  const settingsDirty = Object.keys(draftValues).some(key => {
    return JSON.stringify(draftValues[key]) !== JSON.stringify(getServerValue(key));
  });
  const hasUnsavedChanges = settingsDirty || holidaysDirty;

  // Save all pending changes
  const handleSaveAll = async () => {
    // Prevent double-click submissions
    if (saving) return;

    setError(null);
    setSuccess(null);

    // Validate break allowances if changed
    if ('time_clock.break_allowances' in draftValues) {
      const breaks = draftValues['time_clock.break_allowances'];
      const invalidBreak = breaks?.find(b => !b.name || b.name.trim() === '');
      if (invalidBreak) {
        setError('All breaks must have a name');
        return;
      }
    }

    setSaving(true);
    try {
      // Determine locationId for saving
      // When multi-location company has a location selected, pass locationId
      // This allows location-scoped settings to be saved to location_settings table
      const locationId = locations.length > 1 ? selectedLocation?.id : null;

      // Save holidays before settings drafts: if the holidays API fails
      // partway through, no settings key has been written yet, so a failed
      // Save leaves the server state fully unchanged instead of a partial
      // commit (settings written, holidays lost).
      if (holidaysDirty) {
        await holidaysActionsRef.current.save();
      }

      // Save all changed settings
      for (const [key, value] of Object.entries(draftValues)) {
        if (JSON.stringify(value) !== JSON.stringify(getServerValue(key))) {
          // Find the setting definition to check if it supports location override
          const setting = allSettings.find(s => s.key === key);
          // Only pass locationId if the setting supports location override
          const saveLocationId = setting?.allowLocationOverride ? locationId : null;
          await settingsService.updateSetting(key, value, saveLocationId);
        }
      }

      const locationName = selectedLocation?.name;
      const successMessage = locationId && locationName
        ? `Settings saved for ${locationName}`
        : 'Settings saved successfully';
      setSuccess(successMessage);
      setTimeout(() => setSuccess(null), 3000);
      setDraftValues({});
      await loadSettings();
    } catch (err) {
      console.error('Error saving settings:', err);
      setError(err.response?.data?.error || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // Cancel all pending changes
  const handleCancelAll = () => {
    setDraftValues({});
    if (holidaysDirty) {
      holidaysActionsRef.current.discard();
    }
  };

  // Handle role override update
  const handleRoleOverrideUpdate = async (roleId, key, value) => {
    setError(null);
    setSuccess(null);

    try {
      await settingsService.setRoleOverride(roleId, key, value);
      setSuccess('Role override saved successfully');
      setTimeout(() => setSuccess(null), 3000);
      await loadSettings();
    } catch (err) {
      console.error('Error updating role override:', err);
      setError(err.response?.data?.error || 'Failed to save role override');
    }
  };

  // Handle role override delete
  const handleRoleOverrideDelete = async (roleId, key) => {
    setError(null);
    setSuccess(null);

    try {
      await settingsService.deleteRoleOverride(roleId, key);
      setSuccess('Role override removed');
      setTimeout(() => setSuccess(null), 3000);
      await loadSettings();
    } catch (err) {
      console.error('Error deleting role override:', err);
      setError(err.response?.data?.error || 'Failed to remove role override');
    }
  };

  // Render the active category panel
  const renderPanel = (category) => {
    if (!category) {
      return (
        <div className="flex items-center justify-center h-64 text-gray-500">
          <p>Select a category to view settings</p>
        </div>
      );
    }

    if (category === 'holidays') {
      return (
        <HolidaysSettingsPanel
          saving={saving}
          onDirtyChange={handleHolidaysDirtyChange}
          onRegisterActions={handleRegisterHolidaysActions}
          onError={setError}
          onSuccess={(msg) => {
            setSuccess(msg);
            setTimeout(() => setSuccess(null), 3000);
          }}
        />
      );
    }

    if (!settings[category]) {
      return (
        <div className="flex items-center justify-center h-64 text-gray-500">
          <p>Select a category to view settings</p>
        </div>
      );
    }

    const categorySettings = settings[category];

    switch (category) {
      case 'time_clock':
        return (
          <TimeClockSettingsPanel
            settings={categorySettings}
            getValue={getValue}
            updateDraft={updateDraft}
            saving={saving}
            onRoleOverrideUpdate={handleRoleOverrideUpdate}
            onRoleOverrideDelete={handleRoleOverrideDelete}
            companyState={company?.state}
            selectedLocation={selectedLocation}
            hasMultipleLocations={locations.length > 1}
          />
        );
      case 'pto':
        return (
          <PTOSettingsPanel
            settings={categorySettings}
            getValue={getValue}
            updateDraft={updateDraft}
            saving={saving}
          />
        );
      case 'email_sender':
        return (
          <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-6">
            {/* Display Name */}
            <div>
              <label htmlFor="email-display-name" className="block text-sm font-medium text-gray-900 mb-1">
                Sender Display Name
              </label>
              <p className="text-xs text-gray-500 mb-2">
                The name recipients see in their inbox (e.g., "Acme Corp")
              </p>
              <input
                id="email-display-name"
                type="text"
                value={getValue('email_sender.display_name') ?? ''}
                onChange={(e) => updateDraft('email_sender.display_name', e.target.value)}
                placeholder={company?.businessName || 'Your business name'}
                className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand focus:border-brand"
                disabled={saving}
              />
            </div>

            {/* Reply-To Email */}
            <div>
              <label htmlFor="email-reply-to" className="block text-sm font-medium text-gray-900 mb-1">
                Reply-To Email
              </label>
              <p className="text-xs text-gray-500 mb-2">
                Where customer replies are directed
              </p>
              <input
                id="email-reply-to"
                type="email"
                value={getValue('email_sender.reply_to') ?? ''}
                onChange={(e) => updateDraft('email_sender.reply_to', e.target.value)}
                placeholder="billing@yourbusiness.com"
                className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand focus:border-brand"
                disabled={saving}
              />
            </div>

            {/* From Address (read-only) */}
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-1">
                From Address
              </label>
              <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-500 max-w-md">
                {composeFromEmail}
              </div>
              <p className="text-xs text-gray-400 mt-1">
                Configured in Admin Portal. Custom sending domains available in a future update.
              </p>
            </div>

            {/* Send Copy Default */}
            <div>
              <label htmlFor="email-send-copy-default" className="flex items-center gap-3 cursor-pointer">
                <input
                  id="email-send-copy-default"
                  type="checkbox"
                  checked={getValue('email_sender.send_copy_default') ?? false}
                  onChange={(e) => updateDraft('email_sender.send_copy_default', e.target.checked)}
                  className="rounded border-gray-300 text-brand focus:ring-brand"
                  disabled={saving}
                />
                <div>
                  <span className="text-sm font-medium text-gray-900">Send me a copy by default</span>
                  <p className="text-xs text-gray-500">
                    Pre-check the "Send me a copy" option when sending invoices
                  </p>
                </div>
              </label>
            </div>
          </div>
        );
      case 'invoicing':
        return (
          <div className="bg-white rounded-xl border border-gray-200 p-8">
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Invoicing Settings</h3>
              <p className="text-gray-500 mt-2 max-w-sm mx-auto">
                Configure your default due dates, reminder schedules, and invoice preferences.
              </p>
              <div className="mt-6">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100">
                  Coming Soon
                </span>
              </div>
            </div>
          </div>
        );
      default:
        return (
          <div className="bg-white rounded-xl border border-gray-200 p-8">
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Settings className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                {CATEGORY_CONFIG[category]?.label || category}
              </h3>
              <p className="text-gray-500 mt-2">Settings panel not yet implemented</p>
            </div>
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin w-10 h-10 border-4 border-brand border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-gray-500 font-medium">Loading your settings...</p>
        </div>
      </div>
    );
  }

  return (
    <PermissionGate permission="settings.view" fallback={
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center px-4">
        <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-4">
          <AlertCircle className="w-8 h-8 text-red-500" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900">Access Denied</h3>
        <p className="text-gray-500 mt-2">You don't have permission to view or manage settings.</p>
      </div>
    }>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Header Section */}
        <div className="md:flex md:items-center md:justify-between mb-10">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-4 mb-2">
              <div className="hidden sm:flex p-3 bg-white border border-gray-200 rounded-xl shadow-sm">
                <Settings className="w-6 h-6 text-gray-600" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Settings</h1>
                <p className="text-gray-500 mt-1">
                  Manage your company preferences and workspace configuration.
                </p>
              </div>
            </div>
            {/* Location Selector - shows dropdown when 2+ locations AND category supports location override */}
            <div className="flex items-center gap-2 mt-4 sm:ml-16">
              {loadingLocations ? (
                <div className="h-9 w-48 bg-gray-100 animate-pulse rounded-full" />
              ) : locations.length > 1 && categorySupportsLocationOverride ? (
                <Listbox value={selectedLocation} onChange={setSelectedLocation}>
                  <div className="relative">
                    <Listbox.Button className="relative flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold bg-white text-gray-900 border border-gray-200 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2 transition-colors min-w-[200px]">
                      <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <span className="truncate flex-1 text-left">
                        {selectedLocation?.name || 'Select Location'}
                      </span>
                      {selectedLocation?.state && (
                        <span className="text-xs font-medium text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                          {selectedLocation.state}
                        </span>
                      )}
                      <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    </Listbox.Button>
                    <Transition
                      as={Fragment}
                      leave="transition ease-in duration-100"
                      leaveFrom="opacity-100"
                      leaveTo="opacity-0"
                    >
                      <Listbox.Options className="absolute z-20 mt-2 w-full max-h-60 overflow-auto rounded-xl bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                        {locations.map((location) => (
                          <Listbox.Option
                            key={location.id}
                            value={location}
                            className={({ active, selected }) =>
                              cn(
                                'relative cursor-pointer select-none py-2.5 px-4 transition-colors',
                                active ? 'bg-brand-light' : '',
                                selected ? 'bg-brand-light' : ''
                              )
                            }
                          >
                            {({ selected }) => (
                              <div className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-2 min-w-0">
                                  <MapPin className={cn(
                                    'w-4 h-4 flex-shrink-0',
                                    selected ? 'text-brand' : 'text-gray-400'
                                  )} />
                                  <span className={cn(
                                    'truncate',
                                    selected ? 'font-semibold text-gray-900' : 'text-gray-700'
                                  )}>
                                    {location.name}
                                  </span>
                                  {location.isPrimary && (
                                    <span className="text-[10px] font-bold uppercase tracking-wider bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded flex-shrink-0">
                                      Primary
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  {location.state && (
                                    <span className="text-xs font-medium text-gray-500">
                                      {location.state}
                                    </span>
                                  )}
                                  {hasComplianceRequirements(location.state) && (
                                    <span className="w-2 h-2 rounded-full bg-amber-400" title="Has compliance requirements" />
                                  )}
                                  {selected && (
                                    <Check className="w-4 h-4 text-brand" />
                                  )}
                                </div>
                              </div>
                            )}
                          </Listbox.Option>
                        ))}
                      </Listbox.Options>
                    </Transition>
                  </div>
                </Listbox>
              ) : locations.length > 1 && !categorySupportsLocationOverride ? (
                /* Show "All Locations" when multi-location but category doesn't support location override */
                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold bg-gray-100 text-gray-500 border border-gray-200 cursor-not-allowed">
                  <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <span>All Locations</span>
                </span>
              ) : (
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-semibold bg-gray-100 text-gray-900 border border-gray-200 shadow-sm">
                  <Building2 className="w-4 h-4 text-gray-500" />
                  {company?.businessName || 'Company'}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Alerts */}
        <div className="space-y-4 mb-8">
          {/* Compliance Notice (from location management) */}
          {complianceNotice && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 shadow-sm animate-in fade-in slide-in-from-top-2">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 p-2 bg-blue-100 rounded-lg">
                  <MapPin className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="text-sm font-semibold text-blue-900">
                      {complianceNotice.stateName} Labor Law Compliance
                    </h4>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                      {complianceNotice.state}
                    </span>
                  </div>
                  <p className="text-sm text-blue-800">
                    Your location <span className="font-medium">"{complianceNotice.locationName}"</span> is in a state with specific labor law requirements.
                  </p>
                  <p className="text-sm text-blue-700 mt-2">
                    <Info className="w-4 h-4 inline mr-1" />
                    {complianceNotice.message}
                  </p>
                </div>
                <button
                  onClick={() => setComplianceNotice(null)}
                  className="flex-shrink-0 text-blue-400 hover:text-blue-600 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}

          {error && (
            <div className="flex items-center gap-3 text-red-700 bg-red-50 border border-red-100 px-4 py-4 rounded-xl shadow-sm">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <p className="text-sm font-medium">{error}</p>
              <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          {success && (
            <div className="flex items-center gap-3 text-green-700 bg-green-50 border border-green-100 px-4 py-4 rounded-xl shadow-sm animate-in fade-in slide-in-from-top-2">
              <Check className="w-5 h-5 flex-shrink-0" />
              <p className="text-sm font-medium">{success}</p>
              <button onClick={() => setSuccess(null)} className="ml-auto text-green-400 hover:text-green-600">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        {/* Appearance */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-50 rounded-lg">
                {darkMode ? <Moon className="w-5 h-5 text-gray-600" /> : <Sun className="w-5 h-5 text-yellow-500" />}
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Appearance</h3>
                <p className="text-xs text-gray-500">Switch between dark and light mode</p>
              </div>
            </div>
            <button
              onClick={toggleDarkMode}
              className={cn(
                'relative inline-flex h-8 w-[72px] items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand',
                darkMode ? 'bg-gray-700' : 'bg-brand'
              )}
            >
              <span className="sr-only">Toggle dark mode</span>
              <span
                className={cn(
                  'inline-flex h-6 w-6 items-center justify-center rounded-full bg-white shadow-sm transition-transform',
                  darkMode ? 'translate-x-10' : 'translate-x-1'
                )}
              >
                {darkMode ? <Moon className="w-3.5 h-3.5 text-gray-700" /> : <Sun className="w-3.5 h-3.5 text-yellow-500" />}
              </span>
            </button>
          </div>
        </div>

        {/* Main Content with Headless UI Tabs */}
        <Tab.Group selectedIndex={selectedIndex} onChange={setSelectedIndex} vertical>
          <div className="lg:grid lg:grid-cols-12 lg:gap-10">
            {/* Sidebar Navigation */}
            <aside className="lg:col-span-3 mb-8 lg:mb-0">
              <Tab.List className="flex flex-col space-y-1">
                {visibleCategories.length === 0 ? (
                  <div className="p-6 text-center bg-gray-50 rounded-xl border border-dashed border-gray-300">
                    <p className="text-sm text-gray-500 italic">No settings categories available</p>
                  </div>
                ) : (
                  visibleCategories.map((category) => {
                    const config = CATEGORY_CONFIG[category] || {
                      label: category,
                      icon: Settings,
                      description: ''
                    };
                    const Icon = config.icon;
                    const settingCount = settings[category]?.length || 0;

                    return (
                      <Tab
                        key={category}
                        as={Fragment}
                      >
                        {({ selected }) => (
                          <button
                            className={cn(
                              'group flex items-center gap-3 px-4 py-3 text-left rounded-xl transition-all duration-200 outline-none',
                              selected
                                ? 'bg-white text-gray-900 shadow-sm border border-gray-200 ring-1 ring-black/5'
                                : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50 border border-transparent'
                            )}
                          >
                            <div className={cn(
                              'p-1.5 rounded-lg transition-colors',
                              selected ? 'bg-brand-light text-brand' : 'bg-transparent group-hover:bg-gray-100'
                            )}>
                              <Icon className="w-5 h-5" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold truncate">
                                {config.label}
                              </p>
                              <p className="text-xs opacity-60 font-medium">
                                {category === 'holidays'
                                  ? 'Company calendar'
                                  : `${settingCount} setting${settingCount !== 1 ? 's' : ''}`}
                              </p>
                            </div>
                            <ChevronRight className={cn(
                              'w-4 h-4 transition-transform duration-200',
                              selected ? 'text-gray-400 translate-x-0.5' : 'text-gray-300 opacity-0 group-hover:opacity-100'
                            )} />
                          </button>
                        )}
                      </Tab>
                    );
                  })
                )}
              </Tab.List>
            </aside>

            {/* Settings Panels */}
            <main className="lg:col-span-9">
              <Tab.Panels>
                {visibleCategories.map((category) => (
                  <Tab.Panel
                    key={category}
                    className="outline-none focus:outline-none animate-in fade-in slide-in-from-right-4 duration-500"
                  >
                    <div className="mb-6">
                      <h2 className="text-xl font-bold text-gray-900">
                        {CATEGORY_CONFIG[category]?.label}
                      </h2>
                      <p className="text-sm text-gray-500 mt-1">
                        {CATEGORY_CONFIG[category]?.description}
                      </p>
                    </div>
                    {renderPanel(category)}
                  </Tab.Panel>
                ))}
              </Tab.Panels>
            </main>
          </div>
        </Tab.Group>

        {/* Sticky Save Bar */}
        {hasUnsavedChanges && (
          <div
            className="fixed bottom-6 right-6 left-6 md:left-auto md:right-10 z-50 animate-in fade-in slide-in-from-bottom-10 duration-500"
            style={{
              left: windowWidth >= 1024 ? (sidebarOpen ? 'calc(200px + 2.5rem)' : 'calc(72px + 2.5rem)') : '1.5rem'
            }}
          >
            <div className="max-w-4xl mx-auto bg-gray-900/95 backdrop-blur-md text-white rounded-2xl shadow-2xl border border-white/10 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-brand"></span>
                </div>
                <div>
                  <p className="text-sm font-bold">Unsaved changes</p>
                  <p className="text-xs text-gray-400 hidden sm:block">You have modified settings that haven't been saved yet.</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleCancelAll}
                  disabled={saving}
                  className="px-4 py-2 text-sm font-semibold text-gray-300 hover:text-white transition-colors disabled:opacity-50"
                >
                  Discard
                </button>
                <button
                  type="button"
                  onClick={handleSaveAll}
                  disabled={saving}
                  className="flex items-center gap-2 px-6 py-2.5 bg-brand hover:bg-brand-hover text-white font-bold rounded-xl transition-all shadow-lg shadow-brand/20 disabled:opacity-50 active:scale-95"
                >
                  {saving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-gray-900 border-t-transparent rounded-full animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4" />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </PermissionGate>
  );
};

export default GlobalSettingsPage;
